import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { WarningCircle, ArrowClockwise, Gear, X, Timer } from "@phosphor-icons/react";

const ERROR_CONFIG = {
  timeout: {
    title: "Generation timed out",
    hint: "The AI model took too long. Try a shorter prompt or different model.",
  },
  invalid_key: {
    title: "API key rejected",
    hint: "Your API key was not accepted. Check your key in Settings.",
    showSettings: true,
  },
  rate_limit: {
    title: "Rate limit reached",
    hint: "Too many requests. Wait a moment, then try again.",
  },
  model_unavailable: {
    title: "Model unavailable",
    hint: "The selected model is temporarily down. Try a different one.",
  },
  unknown: {
    title: "Generation failed",
    hint: "Something went wrong. Your inputs are preserved — try again.",
  },
};

const AUTO_RETRY_TYPES = ["timeout", "rate_limit", "model_unavailable"];
const MAX_AUTO_RETRIES = 2;
const RETRY_DELAYS = [5, 15];

export function GenerationErrorBlock({ error, onRetry, onDismiss, autoRetryCount = 0, onAutoRetryStart }) {
  const [countdown, setCountdown] = useState(null);
  const intervalRef = useRef(null);

  const config = ERROR_CONFIG[error?.error_type] || ERROR_CONFIG.unknown;

  const shouldAutoRetry =
    error?.retryable &&
    AUTO_RETRY_TYPES.includes(error?.error_type) &&
    autoRetryCount < MAX_AUTO_RETRIES &&
    typeof onAutoRetryStart === "function";

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!shouldAutoRetry || !error) {
      setCountdown(null);
      return;
    }

    const delay = RETRY_DELAYS[autoRetryCount] ?? RETRY_DELAYS[RETRY_DELAYS.length - 1];
    let remaining = delay;
    setCountdown(remaining);

    intervalRef.current = setInterval(() => {
      remaining--;
      if (remaining <= 0) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        setCountdown(null);
        onAutoRetryStart();
        onRetry();
      } else {
        setCountdown(remaining);
      }
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [error, shouldAutoRetry, autoRetryCount, onAutoRetryStart, onRetry]);

  const cancelAutoRetry = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setCountdown(null);
    onDismiss();
  };

  if (!error) return null;

  const currentDelay = RETRY_DELAYS[autoRetryCount] ?? RETRY_DELAYS[RETRY_DELAYS.length - 1];
  const progress = countdown !== null ? ((currentDelay - countdown) / currentDelay) * 100 : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      className="rounded-lg border border-red-500/30 bg-red-500/5 p-4"
      data-testid="generation-error-block"
    >
      <div className="flex items-start gap-3">
        <WarningCircle className="text-red-400 shrink-0 mt-0.5" size={20} weight="fill" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-red-400" data-testid="generation-error-title">
            {config.title}
          </p>
          <p className="text-xs text-[var(--text-muted)] mt-1 leading-relaxed" data-testid="generation-error-message">
            {error.error_message || config.hint}
          </p>
        </div>
        <button
          onClick={cancelAutoRetry}
          className="shrink-0 p-1 rounded text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors"
          data-testid="generation-error-dismiss"
          aria-label="Dismiss error"
        >
          <X size={14} />
        </button>
      </div>

      {countdown !== null ? (
        <div className="mt-3 ml-8 space-y-2" data-testid="auto-retry-countdown">
          <div className="flex items-center gap-2">
            <Timer size={14} className="text-amber-400 animate-pulse" />
            <span className="text-xs font-medium text-amber-400">
              Retrying in {countdown}s...
            </span>
            <span className="text-[10px] text-[var(--text-muted)] font-['JetBrains_Mono']">
              (attempt {autoRetryCount + 1}/{MAX_AUTO_RETRIES})
            </span>
            <button
              onClick={cancelAutoRetry}
              className="ml-auto text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
              data-testid="auto-retry-cancel"
            >
              Cancel
            </button>
          </div>
          <div className="bg-[var(--bg-hover)] rounded-full h-1">
            <div
              className="bg-amber-400 h-1 rounded-full transition-all duration-1000 ease-linear"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 mt-3 ml-8">
          {error.retryable && (
            <button
              onClick={onRetry}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors"
              data-testid="generation-error-retry"
            >
              <ArrowClockwise size={14} />
              Retry
            </button>
          )}
          {config.showSettings && (
            <a
              href="/settings"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-[var(--bg-raised)] text-[var(--text-secondary)] border border-[var(--border-strong)] hover:text-[#FF4400] hover:border-[#FF4400]/30 transition-colors"
              data-testid="generation-error-settings"
            >
              <Gear size={14} />
              Fix in Settings
            </a>
          )}
          {autoRetryCount >= MAX_AUTO_RETRIES && (
            <span className="text-[10px] text-[var(--text-muted)] font-['JetBrains_Mono']">
              Auto-retry exhausted
            </span>
          )}
        </div>
      )}
    </motion.div>
  );
}
